import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAdminImportDriveToChannelMutation,
  AdminDriveImportStatus,
} from "@/generated/graphql";

type ImportResult = {
  input: string;
  status: AdminDriveImportStatus;
  fileId?: string | null;
  fileName?: string | null;
  downloadId?: string | null;
  error?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: { id: string; name: string };
};

const STATUS_VARIANT: Record<AdminDriveImportStatus, "default" | "secondary" | "destructive" | "outline"> = {
  [AdminDriveImportStatus.Queued]: "default",
  [AdminDriveImportStatus.LinkedExisting]: "secondary",
  [AdminDriveImportStatus.AlreadyLinked]: "outline",
  [AdminDriveImportStatus.Error]: "destructive",
};

const ImportDriveModal = ({ open, onOpenChange, channel }: Props) => {
  const { t } = useTranslation();
  const [urlsText, setUrlsText] = useState("");
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importDrive, { loading }] = useAdminImportDriveToChannelMutation();

  const reset = () => {
    setUrlsText("");
    setResults(null);
    setErrorMessage(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const urls = urlsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (urls.length === 0) return;
    try {
      const { data } = await importDrive({
        variables: { channelId: channel.id, urls },
      });
      setResults((data?.adminImportDriveToChannel ?? []) as ImportResult[]);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const summary = (() => {
    if (!results) return null;
    const queued = results.filter((r) => r.status === AdminDriveImportStatus.Queued).length;
    const linked = results.filter((r) => r.status === AdminDriveImportStatus.LinkedExisting).length;
    const alreadyLinked = results.filter((r) => r.status === AdminDriveImportStatus.AlreadyLinked).length;
    const errors = results.filter((r) => r.status === AdminDriveImportStatus.Error).length;
    return { queued, linked, alreadyLinked, errors };
  })();

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("admin.channels.importModal.title", { channel: channel.name })}
            </DialogTitle>
            <DialogDescription>
              {t("admin.channels.importModal.subtitle")}
            </DialogDescription>
          </DialogHeader>

          {!results ? (
            <Textarea
              rows={8}
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={t("admin.channels.importModal.placeholder")}
              disabled={loading}
            />
          ) : (
            <div className="space-y-3">
              {summary && (
                <p className="text-sm text-muted-foreground">
                  {t("admin.channels.importModal.summary", summary)}
                </p>
              )}
              <div className="max-h-[400px] overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin.channels.importModal.column.input")}</TableHead>
                      <TableHead>{t("admin.channels.importModal.column.fileName")}</TableHead>
                      <TableHead>{t("admin.channels.importModal.column.status")}</TableHead>
                      <TableHead>{t("admin.channels.importModal.column.error")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[260px] truncate" title={r.input}>
                          {r.input}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{r.fileName || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[r.status]}>
                            {t(`admin.channels.importModal.status.${r.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-red-500 text-xs">{r.error || ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            {!results ? (
              <>
                <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>
                  {t("admin.channels.importModal.cancel")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading || urlsText.trim().length === 0}
                >
                  {t("admin.channels.importModal.submit")}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={reset} disabled={loading}>
                  {t("admin.channels.importModal.importMore")}
                </Button>
                <Button onClick={() => handleClose(false)}>
                  {t("admin.channels.importModal.close")}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.channels.importModal.toast.failed")}</DialogTitle>
            <DialogDescription className="break-words">{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorMessage(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportDriveModal;
